import type { TOC } from '@ember/component/template-only';
import type { SafeString } from '@ember/template';
import { PhWarning, PhInfo, PhCheckCircle, PhLightbulb } from 'ember-phosphor-icons';
import { eq } from 'tio-ui/utilities';
import { get } from '@ember/object';

// ---------------------------------------------------------------------------
// AlertBanner – contextual alert/notice banner with icon and optional title
// ---------------------------------------------------------------------------

/**
 * Visual intent/severity options for the alert
 */
export type AlertBannerIntent = 'warning' | 'info' | 'success' | 'tip';

/**
 * Size options for the alert
 */
export type AlertBannerSize = 'sm' | 'md' | 'lg';

interface AlertBannerSignature {
  Args: {
    /**
     * Visual intent/severity of the alert
     * @default "warning"
     */
    intent?: AlertBannerIntent;
    /**
     * Title text for the alert (optional)
     */
    title?: string | SafeString;
    /**
     * Size of the alert
     * @default "md"
     */
    size?: AlertBannerSize;
  };
  Blocks: {
    /**
     * Main content of the alert
     */
    default: [];
    /**
     * Optional action content (button, link)
     */
    action?: [];
  };
  Element: HTMLDivElement;
}

const INTENT_STYLES = {
  warning: {
    container: 'bg-amber-50 border-amber-200 dark:bg-amber-950/50 dark:border-amber-800',
    icon: 'text-amber-600 dark:text-amber-400',
    title: 'text-amber-800 dark:text-amber-200',
    text: 'text-amber-700 dark:text-amber-300',
  },
  info: {
    container: 'bg-blue-50 border-blue-200 dark:bg-blue-950/50 dark:border-blue-800',
    icon: 'text-blue-600 dark:text-blue-400',
    title: 'text-blue-800 dark:text-blue-200',
    text: 'text-blue-700 dark:text-blue-300',
  },
  success: {
    container: 'bg-emerald-50 border-emerald-200 dark:bg-emerald-950/50 dark:border-emerald-800',
    icon: 'text-emerald-600 dark:text-emerald-400',
    title: 'text-emerald-800 dark:text-emerald-200',
    text: 'text-emerald-700 dark:text-emerald-300',
  },
  tip: {
    container: 'bg-violet-50 border-violet-200 dark:bg-violet-950/50 dark:border-violet-800',
    icon: 'text-violet-600 dark:text-violet-400',
    title: 'text-violet-800 dark:text-violet-200',
    text: 'text-violet-700 dark:text-violet-300',
  },
};

const SIZE_STYLES = {
  sm: 'p-3 rounded-lg',
  md: 'p-4 rounded-xl',
  lg: 'p-6 rounded-xl',
};

const ICON_SIZES = {
  sm: 16,
  md: 20,
  lg: 24,
};

/**
 * AlertBanner - Contextual alert/notice banner with icon and optional title
 *
 * Displays informational, warning, success, or tip messages with
 * consistent styling. Supports 4 intents, 3 sizes, and an
 * optional action slot.
 *
 * @example Warning alert
 * ```gts
 * import AlertBanner from 'tio-reimagined/components/alert-banner';
 *
 * <AlertBanner @intent="warning" @title="Important Notice">
 *   Your loan servicer requires verification before processing.
 * </AlertBanner>
 * ```
 *
 * @example Info alert with action
 * ```gts
 * <AlertBanner @intent="info">
 *   You have 3 pending documents to review.
 *   <:action>
 *     <Button @intent="primary" @size="sm">Review Now</Button>
 *   </:action>
 * </AlertBanner>
 * ```
 *
 * @example Tip alert
 * ```gts
 * <AlertBanner @intent="tip" @title="Pro Tip">
 *   Completing your profile increases your match score by 25%.
 * </AlertBanner>
 * ```
 */
const AlertBanner: TOC<AlertBannerSignature> = <template>
  {{#let (if @intent @intent "warning") (if @size @size "md") as |intent size|}}
    {{#let
      (get INTENT_STYLES intent) (get SIZE_STYLES size) (get ICON_SIZES size)
      as |styles sizeClass iconSize|
    }}
      <div
        data-test-alert-banner
        data-test-alert-intent={{intent}}
        class="border {{styles.container}} {{sizeClass}} flex items-start gap-3"
        role="alert"
        ...attributes
      >
        <div class="shrink-0 mt-0.5" data-test-alert-icon>
          {{#if (eq intent "warning")}}
            <PhWarning @size={{iconSize}} class={{styles.icon}} />
          {{else if (eq intent "info")}}
            <PhInfo @size={{iconSize}} class={{styles.icon}} />
          {{else if (eq intent "success")}}
            <PhCheckCircle @size={{iconSize}} class={{styles.icon}} />
          {{else if (eq intent "tip")}}
            <PhLightbulb @size={{iconSize}} class={{styles.icon}} />
          {{/if}}
        </div>
        <div class="flex-1 min-w-0">
          {{#if @title}}
            <div data-test-alert-title class="font-medium {{styles.title}} mb-1">
              {{@title}}
            </div>
          {{/if}}
          <div data-test-alert-content class="text-sm {{styles.text}}">
            {{yield}}
          </div>
        </div>
        {{#if (has-block "action")}}
          <div data-test-alert-action class="shrink-0">
            {{yield to="action"}}
          </div>
        {{/if}}
      </div>
    {{/let}}
  {{/let}}
</template>;

export default AlertBanner;
export type { AlertBannerSignature };
